/**
 * Email thread list configuration.
 * Follows the contactListConfig pattern for generic listing component.
 *
 * Uses declarative column definitions with kind field:
 * - kind: 'object_name' - Renders avatar + display name together (subject line)
 * - kind: 'association' - Renders associated objects using CrmObjectTagGroup (participants)
 * - kind: 'property' - Renders property value using PropertyValueLabel (last activity, message count)
 * - kind: 'email_initiator' - Renders an entity chip for the resolved initiator, or plain
 *   initiator-email text as a fallback
 *
 * Email threads are system-created (not user-created), so createProperties/createAssociations
 * are empty.
 */

import type { ListPageConfig } from '../../core/types';

export const emailThreadListConfig: ListPageConfig = {
  entityType: 'ahoy_email_thread',
  title: 'Email Thread',
  pluralTitle: 'Email Threads',

  // Source integration is low-signal as a default column — it's available via
  // the "+" add-column menu for anyone who syncs multiple mailboxes.
  columns: [
    { kind: 'object_name', sortable: true, width: 280 },
    { kind: 'association', name: 'ahoy_email_thread_to_contact' },
    { kind: 'association', name: 'ahoy_email_thread_to_user' },
    { kind: 'email_initiator' },
    { kind: 'property', name: 'ahoy_email_count', sortable: true },
    { kind: 'property', name: 'ahoy_updated_at' },
  ],

  // No create properties — email threads are system-created
  createProperties: [],
  createAssociations: [],

  // No metrics for email threads
  metrics: [],

  // Default filters: sort by last activity descending
  defaultFilters: {
    page: 1,
    page_size: 20,
    sort: 'ahoy_updated_at',
    order: 'desc',
  },

  // UI configuration
  showTimeFilter: false,

  // Email threads are system-created from synced mail — there's no "create"
  // action, so the empty state nudges the human action that produces threads.
  emptyState: {
    heading: 'No emails yet',
    description: 'Get started by sending your first email.',
  },
};
